import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface BootSequenceProps {
  onComplete: () => void;
}

const BootSequence: React.FC<BootSequenceProps> = ({ onComplete }) => {
  const [step, setStep] = useState(0);

  useEffect(() => {
    const sequence = async () => {
      // Step 1: Initial terminal lines
      await new Promise(r => setTimeout(r, 300));
      setStep(1);
      await new Promise(r => setTimeout(r, 300));
      setStep(2);
      await new Promise(r => setTimeout(r, 300));
      setStep(3);

      // Step 4: Core systems online, engaging hyperdrive
      await new Promise(r => setTimeout(r, 400));
      setStep(4);

      // Step 5: Flash / warp effect
      await new Promise(r => setTimeout(r, 400));
      setStep(5);

      // Finalize and remove
      await new Promise(r => setTimeout(r, 250));
      onComplete();
    };

    sequence();
  }, [onComplete]);

  return (
    <AnimatePresence>
      {step < 5 && (
        <motion.div
          initial={{ opacity: 1 }}
          exit={{ opacity: 0, scale: 1.5, filter: 'blur(10px)' }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: '#050814',
            zIndex: 9999,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            fontFamily: '"Courier New", Courier, monospace',
            color: '#00f0ff',
            textShadow: '0 0 10px rgba(0, 240, 255, 0.5)',
            overflow: 'hidden'
          }}
        >
          <div style={{ width: '300px', textAlign: 'left' }}>
            <motion.p
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: step >= 1 ? 1 : 0, x: step >= 1 ? 0 : -20 }}
              style={{ margin: '8px 0', fontSize: '1.2rem' }}
            >
              &gt; INITIATING NEURAL LINK...
            </motion.p>
            <motion.p
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: step >= 2 ? 1 : 0, x: step >= 2 ? 0 : -20 }}
              style={{ margin: '8px 0', fontSize: '1.2rem' }}
            >
              &gt; LOADING EXPERIENCE DB...
            </motion.p>
            <motion.p
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: step >= 3 ? 1 : 0, x: step >= 3 ? 0 : -20 }}
              style={{ margin: '8px 0', fontSize: '1.2rem', color: '#00ff88', textShadow: '0 0 10px rgba(0, 255, 136, 0.5)' }}
            >
              &gt; SYSTEMS NOMINAL.
            </motion.p>
            
            {step >= 4 && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                style={{
                  marginTop: '2rem',
                  textAlign: 'center',
                  fontSize: '1.5rem',
                  fontWeight: 'bold',
                  color: '#ff003c',
                  textShadow: '0 0 15px rgba(255, 0, 60, 0.8)'
                }}
              >
                ENGAGING HYPERDRIVE
              </motion.div>
            )}
          </div>
          
          {/* Flash Overlay */}
          {step === 4 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: [0, 1, 0] }}
              transition={{ duration: 0.8, times: [0, 0.5, 1] }}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                backgroundColor: 'white',
                pointerEvents: 'none',
                mixBlendMode: 'overlay'
              }}
            />
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default BootSequence;
